// DOC-DEPS: LLM.md -> docs/PROJECT_MAP.md -> src/features/chat/README.md
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { supabase } from '../../../api/supabase';
import { getSupabaseSession } from '../../../lib/supabase-utils';
import { toLocalDateStr, getYesterdayStr } from '../../../lib/dateUtils';
import { isLegacyChatActivityType } from '../../../lib/activityType';
import { cn } from '../../../lib/utils';
import { APP_MODAL_CARD_CLASS } from '../../../lib/modalTheme';
import type { Message } from '../../../store/useChatStore';
import { mapDbRowToMessage } from '../../../store/chatHelpers';
import { getScopedClientStorageKey, resolveStorageScopeForUser } from '../../../store/storageScope';

const STORAGE_KEY = 'yesterday_popup_date';

export const YesterdaySummaryPopup: React.FC = () => {
  const { t } = useTranslation();
  const [event, setEvent] = useState<Message | null>(null);
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    (async () => {
      const session = await getSupabaseSession();
      if (!session) return;
      const today = toLocalDateStr(new Date());
      const storageKey = getScopedClientStorageKey(STORAGE_KEY, resolveStorageScopeForUser(session.user.id));
      if (localStorage.getItem(storageKey) === today) return;

      const yesterdayStr = getYesterdayStr();
      const dayStart = new Date(yesterdayStr + 'T00:00:00').getTime();
      const dayEnd = new Date(yesterdayStr + 'T23:59:59.999').getTime();

      const { data } = await supabase
        .from('messages')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('is_mood', false)
        .gte('timestamp', dayStart)
        .lte('timestamp', dayEnd)
        .order('timestamp', { ascending: false })
        .limit(1);

      const latestRuntimeRow = data?.find((row) => !isLegacyChatActivityType(row.activity_type));
      if (latestRuntimeRow) {
        setEvent(mapDbRowToMessage(latestRuntimeRow));
        // 成功后才写，失败不记录，下次还会弹
        localStorage.setItem(storageKey, today);
      }
    })();
  }, []);

  // 5 秒自动关闭（用户点击保持）
  useEffect(() => {
    if (!event || userInteracted) return;
    const timer = setTimeout(() => setEvent(null), 5000);
    return () => clearTimeout(timer);
  }, [event, userInteracted]);

  return (
    <AnimatePresence>
      {event && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.25 }}
          className="fixed left-4 right-4 z-30 pointer-events-auto"
          style={{ top: 'max(12px, calc(env(safe-area-inset-top, 0px) + 8px))' }}
          onPointerDown={() => setUserInteracted(true)}
        >
          <div
            className={cn(APP_MODAL_CARD_CLASS, 'rounded-2xl px-4 py-3 flex items-start gap-3')}
            style={{
              background:
                'linear-gradient(140deg, rgba(168,85,247,0.38) 0%, rgba(139,92,246,0.30) 45%, rgba(217,70,239,0.24) 100%)',
              border: '1px solid rgba(255,255,255,0.38)',
              backdropFilter: 'blur(18px) saturate(155%)',
              WebkitBackdropFilter: 'blur(18px) saturate(155%)',
              boxShadow: '0 10px 30px rgba(88,28,135,0.26), inset 0 1px 1px rgba(255,255,255,0.35)',
            }}
          >
            <div className="text-2xl mt-0.5">🌙</div>
            <div className="flex-1 min-w-0">
              <p className="app-caption mb-0.5 font-semibold text-violet-50/95">{t('yesterday_popup')}</p>
              <p className="app-body truncate font-medium text-white/95">{event.content}</p>
              {event.duration != null && (
                <p className="app-caption mt-0.5 text-violet-100/80">
                  {Math.round(event.duration)} min
                </p>
              )}
            </div>
            <button
              onClick={() => setEvent(null)}
              className="mt-0.5 rounded-full p-1 text-violet-50/85 transition-colors hover:text-white"
              style={{ background: 'rgba(255,255,255,0.12)' }}
            >
              <X size={13} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
